import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { PostLoginAuthLink } from "@/components/routing/PostLoginAuthLink";
import { ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { LANDING_CTA_SUBHEADLINE } from "@/lib/landingMarketing";

/**
 * Renders a call-to-action section for starting a financial journey.
 * @example
 * CallToActionSection()
 * <section className="container mx-auto px-4 py-8 md:py-12 text-center">...</section>
 * @returns {JSX.Element} A JSX element representing the call-to-action section.
 * @description
 *   - The section includes a headline, a brief description, and a button that links to the signup page.
 *   - Button component utilizes 'asChild' prop to render the Link component.
 *   - Layout adjusts for responsiveness with CSS classes targeting different screen sizes.
 */
export function CallToActionSection() {
  const { user } = useAuth();
  const isLoggedIn = !!user;

  return (
    <section className="container mx-auto px-4 py-16 md:py-24">
      <div className="max-w-4xl mx-auto text-center bg-gradient-to-br from-orange-50 to-white border-2 border-orange-200 rounded-3xl p-8 md:p-12 shadow-lg">
        <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 text-gray-900">
          {isLoggedIn ? "Continue Your Wealth Journey" : "Start Your Wealth Journey Today"}
        </h2>
        <p className="text-lg md:text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
          {isLoggedIn
            ? "Pick up where you left off and keep building your financial future with TellerWand."
            : LANDING_CTA_SUBHEADLINE}
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
          <Button
            size="lg"
            className="w-full sm:w-auto bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white shadow-lg hover:shadow-xl transition-all"
            asChild
          >
            {isLoggedIn ? (
              <Link to="/dashboard">
                Go to Dashboard
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            ) : (
              <PostLoginAuthLink mode="signup">
                Get Started Free
                <ArrowRight className="ml-2 h-5 w-5" />
              </PostLoginAuthLink>
            )}
          </Button>
          {!isLoggedIn && <p className="text-sm text-gray-500">No credit card required</p>}
        </div>
      </div>
    </section>
  );
}
